import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import TelegramChatPickerModal from "./TelegramChatPickerModal";

function buildTelegramUserName(displayName) {
  const slug = (displayName || "").replace(/\s+/g, "").trim();
  return slug ? `@${slug}` : null;
}

function sortedIds(arr) {
  return [...arr].sort((a, b) => a - b);
}

function idsEqual(a, b) {
  if (a.length !== b.length) return false;
  const sa = sortedIds(a);
  const sb = sortedIds(b);
  return sa.every((v, i) => v === sb[i]);
}

function initials(name, email) {
  const base = (name || email || "?").trim();
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

function extractTargetIds(user) {
  return (user?.targets || []).map((t) => (typeof t === "object" ? t.id : t));
}

function snapshotFromUser(user) {
  return {
    email: user.email ?? "",
    displayName: user.displayName ?? "",
    isActive: !!user.isActive,
    isAdministrator: !!user.isAdministrator,
    canDeployAll: !!user.canDeployAll,
    canUseTelegramBot: !!user.canUseTelegramBot,
    canViewAllDeployments: !!user.canViewAllDeployments,
    canSelectDeploymentNotificationRecipients: !!user.canSelectDeploymentNotificationRecipients,
    telegramPhoneNumber: user.telegramPhoneNumber ?? "",
    telegramChatId: user.telegramChatId ?? "",
    whatsappPhoneNumber: user.whatsappPhoneNumber ?? "",
    notifyEmailOnDeploymentCompletion: !!user.notifyEmailOnDeploymentCompletion,
    notifyTelegramOnDeploymentCompletion: !!user.notifyTelegramOnDeploymentCompletion,
    notifyWhatsappOnDeploymentCompletion: !!user.notifyWhatsappOnDeploymentCompletion,
    targetIds: extractTargetIds(user),
  };
}

const BLANK_SNAPSHOT = {
  email: "",
  displayName: "",
  isActive: true,
  isAdministrator: false,
  canDeployAll: false,
  canUseTelegramBot: false,
  canViewAllDeployments: false,
  canSelectDeploymentNotificationRecipients: false,
  telegramPhoneNumber: "",
  telegramChatId: "",
  whatsappPhoneNumber: "",
  notifyEmailOnDeploymentCompletion: false,
  notifyTelegramOnDeploymentCompletion: false,
  notifyWhatsappOnDeploymentCompletion: false,
  targetIds: [],
};

const PRIMITIVE_KEYS = [
  "email", "displayName",
  "isActive", "isAdministrator", "canDeployAll",
  "canUseTelegramBot", "canViewAllDeployments", "canSelectDeploymentNotificationRecipients",
  "telegramPhoneNumber", "telegramChatId", "whatsappPhoneNumber",
  "notifyEmailOnDeploymentCompletion", "notifyTelegramOnDeploymentCompletion", "notifyWhatsappOnDeploymentCompletion",
];

function snapshotsEqual(a, b) {
  if (!a || !b) return a === b;
  for (const k of PRIMITIVE_KEYS) {
    if (a[k] !== b[k]) return false;
  }
  return idsEqual(a.targetIds, b.targetIds);
}

function validateForm(form) {
  if (!(form.displayName || "").trim()) return "Informe o nome de exibição.";
  if (!(form.email || "").trim()) return "Informe o e-mail.";
  if (form.notifyTelegramOnDeploymentCompletion && !(form.telegramChatId || "").trim()) {
    return "Para notificar via Telegram, vincule um chat na seção Integrações.";
  }
  if (form.notifyWhatsappOnDeploymentCompletion && !(form.whatsappPhoneNumber || "").trim()) {
    return "Para notificar via WhatsApp, informe o telefone na seção Integrações.";
  }
  const hasDeploymentAccess =
    form.isAdministrator ||
    form.canDeployAll ||
    (form.targetIds || []).length > 0;
  const receivesDeploymentNotification =
    form.notifyEmailOnDeploymentCompletion ||
    form.notifyTelegramOnDeploymentCompletion ||
    form.notifyWhatsappOnDeploymentCompletion;
  if (!hasDeploymentAccess && !receivesDeploymentNotification) {
    return "Selecione pelo menos um ambiente, ative 'Acesso total aos ambientes' ou habilite uma notificação.";
  }
  return null;
}

function toPayload(form) {
  const emptyToNull = (v) => (v && String(v).trim() ? v : null);
  return {
    email: form.email,
    displayName: form.displayName,
    isActive: form.isActive,
    isAdministrator: form.isAdministrator,
    canDeployAll: form.canDeployAll,
    canUseTelegramBot: form.canUseTelegramBot,
    canViewAllDeployments: form.canViewAllDeployments,
    canSelectDeploymentNotificationRecipients: form.canSelectDeploymentNotificationRecipients,
    telegramPhoneNumber: emptyToNull(form.telegramPhoneNumber),
    telegramChatId: emptyToNull(form.telegramChatId),
    telegramUserName: buildTelegramUserName(form.displayName),
    whatsappPhoneNumber: emptyToNull(form.whatsappPhoneNumber),
    notifyEmailOnDeploymentCompletion: form.notifyEmailOnDeploymentCompletion,
    notifyTelegramOnDeploymentCompletion: form.notifyTelegramOnDeploymentCompletion,
    notifyWhatsappOnDeploymentCompletion: form.notifyWhatsappOnDeploymentCompletion,
    targetIds: form.targetIds,
  };
}

function VioletSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
        checked ? "bg-violet-600" : "bg-slate-300"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform translate-y-1 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function ToggleRow({ label, description, checked, onChange, disabled, badge, title }) {
  return (
    <label
      title={title}
      className={`flex items-start justify-between gap-4 p-4 rounded-xl ring-1 transition ${
      checked ? "bg-violet-50/60 ring-violet-200" : "bg-slate-50/50 ring-slate-200"
    } ${disabled ? "" : "cursor-pointer"}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-slate-800">{label}</p>
          {badge && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-amber-100 text-amber-700 ring-1 ring-amber-200">
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              {badge}
            </span>
          )}
        </div>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
      <VioletSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </label>
  );
}

function FieldLabel({ children }) {
  return (
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
      {children}
    </label>
  );
}

function TextField({ label, value, onChange, type = "text", placeholder, autoComplete, disabled, required }) {
  return (
    <div>
      <FieldLabel>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</FieldLabel>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        required={required}
        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400
                   focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition
                   disabled:opacity-60 disabled:cursor-not-allowed"
      />
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="px-6 py-5 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 rounded-md bg-violet-100 text-violet-600 flex items-center justify-center">
          {icon}
        </div>
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
      </div>
      {children}
    </section>
  );
}

export default function UsuariosTargets() {
  const [usuarios, setUsuarios] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(true);
  const [loadingTargets, setLoadingTargets] = useState(true);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [chatPickerOpen, setChatPickerOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API}/admin/users`)
      .then((res) => setUsuarios(res.data || []))
      .catch((err) => {
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao carregar usuários";
        AlertComponent("Erro", mensagem, { icon: "error" });
      })
      .finally(() => setLoadingUsuarios(false));

    axios
      .get(`${process.env.REACT_APP_API}/deploy-targets`)
      .then((res) => setTargets(res.data || []))
      .catch((err) => {
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao carregar ambientes";
        AlertComponent("Erro", mensagem, { icon: "error" });
      })
      .finally(() => setLoadingTargets(false));
  }, []);

  const selectedUser = useMemo(
    () => usuarios.find((u) => u.id === selectedUserId) || null,
    [usuarios, selectedUserId]
  );

  const isDirty = useMemo(
    () => form != null && original != null && !snapshotsEqual(form, original),
    [form, original]
  );

  const applyUser = (user) => {
    const snap = snapshotFromUser(user);
    setCreating(false);
    setSelectedUserId(user.id);
    setForm(snap);
    setOriginal(snap);
  };

  const enterCreateMode = () => {
    setCreating(true);
    setSelectedUserId(null);
    setForm(BLANK_SNAPSHOT);
    setOriginal(BLANK_SNAPSHOT);
  };

  const confirmDiscardOrSave = async () => {
    return AlertComponent(
      "Alterações não salvas",
      creating
        ? "As informações do novo usuário serão perdidas. Deseja salvar ou descartar as alterações?"
        : "As informações deste usuário foram alteradas. Deseja salvar ou descartar as alterações?",
      {
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Salvar",
        cancelButtonText: "Descartar",
        confirmButtonColor: "#7c3aed",
        cancelButtonColor: "#64748b",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCloseButton: false,
        reverseButtons: true,
      },
      null,
      false
    );
  };

  const saveUser = async (user, formSnap, isCreate) => {
    const erro = validateForm(formSnap);
    if (erro) {
      AlertComponent("Atenção", erro, { icon: "warning" });
      return null;
    }
    setSaving(true);
    try {
      const newTargets = targets.filter((t) => formSnap.targetIds.includes(t.id));

      if (isCreate) {
        const res = await axios.post(
          `${process.env.REACT_APP_API}/admin/users`,
          toPayload(formSnap)
        );
        const created = res.data || {};
        const enriched = {
          email: formSnap.email,
          displayName: formSnap.displayName,
          isActive: formSnap.isActive,
          isAdministrator: formSnap.isAdministrator,
          canDeployAll: formSnap.canDeployAll,
          canUseTelegramBot: formSnap.canUseTelegramBot,
          canViewAllDeployments: formSnap.canViewAllDeployments,
          canSelectDeploymentNotificationRecipients: formSnap.canSelectDeploymentNotificationRecipients,
          telegramPhoneNumber: formSnap.telegramPhoneNumber || null,
          telegramChatId: formSnap.telegramChatId || null,
          telegramUserName: buildTelegramUserName(formSnap.displayName),
          telegramLinkedAt: null,
          whatsappPhoneNumber: formSnap.whatsappPhoneNumber || null,
          notifyEmailOnDeploymentCompletion: formSnap.notifyEmailOnDeploymentCompletion,
          notifyTelegramOnDeploymentCompletion: formSnap.notifyTelegramOnDeploymentCompletion,
          notifyWhatsappOnDeploymentCompletion: formSnap.notifyWhatsappOnDeploymentCompletion,
          targets: newTargets,
          ...created,
        };
        setUsuarios((prev) => [enriched, ...prev]);
        AlertComponent("Sucesso", "Usuário criado com sucesso", { icon: "success" });
        return enriched;
      }

      await axios.put(
        `${process.env.REACT_APP_API}/admin/users/${user.id}`,
        toPayload(formSnap)
      );
      const updated = {
        ...user,
        email: formSnap.email,
        displayName: formSnap.displayName,
        isActive: formSnap.isActive,
        isAdministrator: formSnap.isAdministrator,
        canDeployAll: formSnap.canDeployAll,
        canUseTelegramBot: formSnap.canUseTelegramBot,
        canViewAllDeployments: formSnap.canViewAllDeployments,
        canSelectDeploymentNotificationRecipients: formSnap.canSelectDeploymentNotificationRecipients,
        telegramPhoneNumber: formSnap.telegramPhoneNumber || null,
        telegramChatId: formSnap.telegramChatId || null,
        telegramUserName: buildTelegramUserName(formSnap.displayName),
        whatsappPhoneNumber: formSnap.whatsappPhoneNumber || null,
        notifyEmailOnDeploymentCompletion: formSnap.notifyEmailOnDeploymentCompletion,
        notifyTelegramOnDeploymentCompletion: formSnap.notifyTelegramOnDeploymentCompletion,
        notifyWhatsappOnDeploymentCompletion: formSnap.notifyWhatsappOnDeploymentCompletion,
        targets: newTargets,
      };
      setUsuarios((prev) => prev.map((u) => (u.id === user.id ? updated : u)));
      setOriginal(formSnap);
      AlertComponent("Sucesso", "Alterações salvas com sucesso", { icon: "success" });
      return updated;
    } catch (err) {
      const mensagem = err?.response?.data?.message || err?.message || "Erro ao salvar";
      AlertComponent("Erro", mensagem, { icon: "error" });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSelectUser = async (user) => {
    if (!creating && user.id === selectedUserId) return;

    if (isDirty && form) {
      const result = await confirmDiscardOrSave();
      if (result?.isConfirmed) {
        const saved = await saveUser(selectedUser, form, creating);
        if (!saved) return;
      } else if (!result?.dismiss) {
        return;
      }
    }

    applyUser(user);
  };

  const handleNewUser = async () => {
    if (creating && !isDirty) return;

    if (isDirty && form) {
      const result = await confirmDiscardOrSave();
      if (result?.isConfirmed) {
        const saved = await saveUser(selectedUser, form, creating);
        if (!saved) return;
      } else if (!result?.dismiss) {
        return;
      }
    }

    enterCreateMode();
  };

  const updateField = (key, value) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const toggleTarget = (targetId) => {
    setForm((prev) => {
      if (!prev) return prev;
      const has = prev.targetIds.includes(targetId);
      return {
        ...prev,
        targetIds: has ? prev.targetIds.filter((id) => id !== targetId) : [...prev.targetIds, targetId],
      };
    });
  };

  const handleSave = async () => {
    if (!form || !isDirty) return;
    if (creating) {
      const created = await saveUser(null, form, true);
      if (created) applyUser(created);
    } else if (selectedUser) {
      await saveUser(selectedUser, form, false);
    }
  };

  const targetsAtivos = useMemo(() => targets.filter((t) => t.isActive !== false), [targets]);
  const isNotificationOnlyUser =
    !!form &&
    !form.isAdministrator &&
    !form.canDeployAll &&
    form.targetIds.length === 0 &&
    (form.notifyEmailOnDeploymentCompletion ||
      form.notifyTelegramOnDeploymentCompletion ||
      form.notifyWhatsappOnDeploymentCompletion);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-6 relative overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
      >
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }} />
        <div className="absolute -bottom-12 -left-6 w-40 h-40 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#c4b5fd,transparent)" }} />
        <div className="relative">
          <p className="text-purple-300 text-sm font-medium mb-1">Administração</p>
          <h1 className="text-2xl sm:text-3xl font-bold">Usuários</h1>
          <p className="text-purple-300 text-sm mt-1">Selecione um usuário à esquerda para editar seus dados e os ambientes acessíveis.</p>
        </div>
      </div>

      {/* Split layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-6">
        {/* Esquerda: usuários */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-violet-500" />
              <h2 className="text-base font-bold text-slate-800">Usuários</h2>
              {!loadingUsuarios && (
                <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700 rounded-full">
                  {usuarios.length}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleNewUser}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Novo
            </button>
          </div>

          {loadingUsuarios ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : usuarios.length === 0 && !creating ? (
            <div className="text-center py-16 px-6">
              <p className="text-slate-500 font-medium">Nenhum usuário encontrado</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 overflow-y-auto max-h-[calc(100vh-280px)]">
              {creating && (
                <li>
                  <div className="w-full text-left flex items-center gap-3 px-5 py-3.5 bg-violet-50 ring-1 ring-inset ring-violet-200">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 ring-2 ring-violet-300 ring-offset-2"
                      style={{ background: "linear-gradient(135deg,#a78bfa,#7c3aed)" }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-violet-900 truncate">
                          {(form?.displayName || "").trim() || "Novo usuário"}
                        </p>
                        {isDirty && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-orange-100 text-orange-700 ring-1 ring-orange-200">
                            Não salvo
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-violet-600 truncate mt-0.5">
                        {(form?.email || "").trim() || "Preencha os dados à direita"}
                      </p>
                    </div>
                  </div>
                </li>
              )}
              {usuarios.map((u) => {
                const isSelected = u.id === selectedUserId;
                const isDirtyHere = isSelected && isDirty;
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => handleSelectUser(u)}
                      className={`w-full text-left flex items-center gap-3 px-5 py-3.5 transition-colors ${
                        isSelected
                          ? "bg-violet-50 ring-1 ring-inset ring-violet-200"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${
                          isSelected ? "ring-2 ring-violet-300 ring-offset-2" : ""
                        }`}
                        style={{ background: "linear-gradient(135deg,#a78bfa,#7c3aed)" }}
                      >
                        {initials(u.displayName, u.email)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-semibold truncate ${isSelected ? "text-violet-900" : "text-slate-800"}`}>
                            {u.displayName || u.email || "(sem nome)"}
                          </p>
                          {u.isAdministrator && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-amber-100 text-amber-700 ring-1 ring-amber-200">
                              Admin
                            </span>
                          )}
                          {u.canDeployAll && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-violet-100 text-violet-700 ring-1 ring-violet-200">
                              Acesso total
                            </span>
                          )}
                          {!u.isActive && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                              Inativo
                            </span>
                          )}
                          {isDirtyHere && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-orange-100 text-orange-700 ring-1 ring-orange-200">
                              Não salvo
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{u.email}</p>
                      </div>
                      <svg className={`w-4 h-4 shrink-0 transition-colors ${isSelected ? "text-violet-500" : "text-slate-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {chatPickerOpen && (
          <TelegramChatPickerModal
            currentUserId={creating ? null : selectedUser?.id}
            currentChatId={form?.telegramChatId || null}
            onSelect={(chat) => {
              updateField("telegramChatId", String(chat.chatId));
              setChatPickerOpen(false);
            }}
            onClose={() => setChatPickerOpen(false)}
          />
        )}

        {/* Direita: edição do usuário */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2 h-2 rounded-full bg-violet-500 shrink-0" />
              <h2 className="text-base font-bold text-slate-800 shrink-0">
                {creating ? "Novo usuário" : "Detalhes"}
              </h2>
              {!creating && selectedUser && (
                <span className="ml-1 text-xs text-slate-500 truncate">
                  · {selectedUser.displayName || selectedUser.email}
                </span>
              )}
            </div>
            {isDirty && (
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-orange-100 text-orange-700 ring-1 ring-orange-200 shrink-0">
                Não salvo
              </span>
            )}
          </div>

          {!form ? (
            <div className="flex-1 text-center py-20 px-6">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                </svg>
              </div>
              <p className="text-slate-500 font-medium mb-1">Selecione um usuário</p>
              <p className="text-slate-400 text-sm">Ou clique em <strong className="text-violet-600">Novo</strong> para criar um novo usuário.</p>
            </div>
          ) : (
            <>
              <div className="overflow-y-auto flex-1 max-h-[calc(100vh-340px)]">
                {/* Identidade */}
                <Section
                  title="Identidade"
                  icon={
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  }
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <TextField
                      label="Nome de exibição"
                      value={form.displayName}
                      onChange={(v) => updateField("displayName", v)}
                      placeholder="Fábio"
                      disabled={saving}
                      required
                    />
                    <TextField
                      label="E-mail"
                      type="email"
                      value={form.email}
                      onChange={(v) => updateField("email", v)}
                      placeholder="usuario@exemplo.com"
                      autoComplete="off"
                      disabled={saving}
                      required
                    />
                  </div>
                </Section>

                {/* Permissões */}
                <Section
                  title="Permissões"
                  icon={
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  }
                >
                  <div className="grid grid-cols-1 gap-3">
                    <ToggleRow
                      label="Usuário ativo"
                      description="Desative para impedir o login e uso da conta."
                      checked={form.isActive}
                      onChange={(v) => updateField("isActive", v)}
                      disabled={saving}
                    />
                    <ToggleRow
                      label="Administrador"
                      description="Concede acesso total à área administrativa do sistema."
                      checked={form.isAdministrator}
                      onChange={(v) => updateField("isAdministrator", v)}
                      disabled={saving}
                    />
                    <ToggleRow
                      label="Acesso total aos ambientes"
                      description={form.isAdministrator
                        ? "Já incluso no perfil Administrador."
                        : "Quando ativo, o usuário pode realizar deploys em todos os ambientes. A lista de ambientes deixa de ser necessária."}
                      checked={form.isAdministrator || form.canDeployAll}
                      onChange={(v) => updateField("canDeployAll", v)}
                      disabled={saving || form.isAdministrator}
                      badge={form.isAdministrator ? "Admin" : undefined}
                      title={form.isAdministrator ? "Esta permissão é concedida automaticamente para administradores." : undefined}
                    />
                    <ToggleRow
                      label="Visualizar deploys de outros usuários"
                      description={form.isAdministrator
                        ? "Já incluso no perfil Administrador."
                        : "Permite enxergar deploys realizados por outras pessoas, não apenas os próprios."}
                      checked={form.isAdministrator || form.canViewAllDeployments}
                      onChange={(v) => updateField("canViewAllDeployments", v)}
                      disabled={saving || form.isAdministrator}
                      badge={form.isAdministrator ? "Admin" : undefined}
                      title={form.isAdministrator ? "Esta permissão é concedida automaticamente para administradores." : undefined}
                    />
                    <ToggleRow
                      label="Acionar deploys via bot do Telegram"
                      description={form.isAdministrator
                        ? "Já incluso no perfil Administrador."
                        : !form.telegramChatId
                          ? "Configure os dados do Telegram em Integrações para habilitar."
                          : "Permite usar comandos do bot do Telegram para iniciar e acompanhar deploys."}
                      checked={!!form.telegramChatId && (form.isAdministrator || form.canUseTelegramBot)}
                      onChange={(v) => updateField("canUseTelegramBot", v)}
                      disabled={saving || form.isAdministrator || !form.telegramChatId}
                      badge={form.isAdministrator && form.telegramChatId ? "Admin" : undefined}
                      title={form.isAdministrator
                        ? "Esta permissão é concedida automaticamente para administradores."
                        : !form.telegramChatId
                          ? "Configure o Telegram em Integrações para habilitar."
                          : undefined}
                    />
                    <ToggleRow
                      label="Selecionar destinatários das notificações de deploy"
                      description={form.isAdministrator
                        ? "Já incluso no perfil Administrador."
                        : "Permite escolher quais usuários receberão notificação ao iniciar um deploy."}
                      checked={form.isAdministrator || form.canSelectDeploymentNotificationRecipients}
                      onChange={(v) => updateField("canSelectDeploymentNotificationRecipients", v)}
                      disabled={saving || form.isAdministrator}
                      badge={form.isAdministrator ? "Admin" : undefined}
                      title={form.isAdministrator ? "Esta permissão é concedida automaticamente para administradores." : undefined}
                    />
                  </div>
                </Section>

                {/* Notificações */}
                <Section
                  title="Notificações"
                  icon={
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  }
                >
                  <div className="grid grid-cols-1 gap-3">
                    <ToggleRow
                      label="Notificar por e-mail ao concluir um deploy"
                      description="Envia um e-mail para o endereço cadastrado quando o deploy concluir."
                      checked={form.notifyEmailOnDeploymentCompletion}
                      onChange={(v) => updateField("notifyEmailOnDeploymentCompletion", v)}
                      disabled={saving}
                    />
                    <ToggleRow
                      label="Notificar por Telegram ao concluir um deploy"
                      description={!form.telegramChatId
                        ? "Configure os dados do Telegram em Integrações para habilitar."
                        : "Envia uma mensagem no Telegram quando o deploy concluir."}
                      checked={!!form.telegramChatId && form.notifyTelegramOnDeploymentCompletion}
                      onChange={(v) => updateField("notifyTelegramOnDeploymentCompletion", v)}
                      disabled={saving || !form.telegramChatId}
                      title={!form.telegramChatId ? "Configure o Telegram em Integrações para habilitar." : undefined}
                    />
                    <ToggleRow
                      label="Notificar por WhatsApp ao concluir um deploy"
                      description={!form.whatsappPhoneNumber
                        ? "Informe o telefone do WhatsApp em Integrações para habilitar."
                        : "Envia uma mensagem no WhatsApp um minuto depois que o deploy concluir."}
                      checked={!!form.whatsappPhoneNumber && form.notifyWhatsappOnDeploymentCompletion}
                      onChange={(v) => updateField("notifyWhatsappOnDeploymentCompletion", v)}
                      disabled={saving || !form.whatsappPhoneNumber}
                      title={!form.whatsappPhoneNumber ? "Informe o telefone do WhatsApp em Integrações para habilitar." : undefined}
                    />
                  </div>
                </Section>

                {/* Integrações */}
                <Section
                  title="Integrações"
                  icon={
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  }
                >
                  <div className="p-4 rounded-xl ring-1 ring-emerald-200 bg-emerald-50/40 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498A1 1 0 0121 15.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">WhatsApp</p>
                    </div>
                    <p className="text-xs text-slate-600 mb-4">
                      Use um número com DDI e DDD para receber notificações pelo gateway de WhatsApp.
                    </p>
                    <TextField
                      label="Telefone do WhatsApp"
                      value={form.whatsappPhoneNumber}
                      onChange={(v) => setForm((prev) => prev ? {
                        ...prev,
                        whatsappPhoneNumber: v,
                        notifyWhatsappOnDeploymentCompletion: v ? prev.notifyWhatsappOnDeploymentCompletion : false,
                      } : prev)}
                      placeholder="+55 11 99999-9999"
                      disabled={saving}
                    />
                  </div>

                  <div className="p-4 rounded-xl ring-1 ring-violet-200 bg-violet-50/40">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      <p className="text-xs font-bold text-violet-800 uppercase tracking-wider">Telegram</p>
                    </div>
                    <p className="text-xs text-slate-600 mb-4">
                      Vincule o chat do usuário para liberar notificações por Telegram e/ou o bot de deploy.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <TextField
                        label="Telefone"
                        value={form.telegramPhoneNumber}
                        onChange={(v) => updateField("telegramPhoneNumber", v)}
                        placeholder="+55 11 99999-9999"
                        disabled={saving}
                      />
                      <div>
                        <FieldLabel>Chat do Telegram</FieldLabel>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setChatPickerOpen(true)}
                            disabled={saving}
                            className="px-4 py-2.5 rounded-xl text-xs font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 ring-1 ring-violet-200 transition disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {form.telegramChatId ? "Alterar chat vinculado" : "Selecionar chat"}
                          </button>
                          {form.telegramChatId && (
                            <button
                              type="button"
                              onClick={() => setForm((prev) => prev ? {
                                ...prev,
                                telegramChatId: "",
                                notifyTelegramOnDeploymentCompletion: false,
                                canUseTelegramBot: false,
                              } : prev)}
                              disabled={saving}
                              className="px-4 py-2.5 rounded-xl text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 ring-1 ring-red-200 transition disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              Desvincular
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {!creating && selectedUser?.telegramLinkedAt && form.telegramChatId && (
                      <p className="mt-3 text-xs text-slate-500">
                        Vinculado em {new Date(selectedUser.telegramLinkedAt).toLocaleString("pt-BR", {
                          day: "2-digit", month: "2-digit", year: "numeric",
                          hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo",
                        })}
                      </p>
                    )}
                    {process.env.REACT_APP_TELEGRAM_BOT_URL && (
                      <p className="mt-3 text-xs text-slate-600">
                        Antes de vincular, peça para o usuário iniciar uma conversa com o bot em{" "}
                        <a
                          href={process.env.REACT_APP_TELEGRAM_BOT_URL}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="font-semibold text-violet-700 hover:text-violet-900 underline decoration-violet-300 hover:decoration-violet-500"
                        >
                          {process.env.REACT_APP_TELEGRAM_BOT_URL}
                        </a>
                        {" "}e enviar <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">/start</code>.
                      </p>
                    )}
                  </div>
                </Section>

                {/* Ambientes */}
                <Section
                  title="Ambientes acessíveis"
                  icon={
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                    </svg>
                  }
                >
                  {form.isAdministrator || form.canDeployAll ? (
                    <div className="px-4 py-3 rounded-xl bg-violet-50 ring-1 ring-violet-100 flex items-start gap-3">
                      <svg className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs text-violet-800">
                        {form.isAdministrator
                          ? <>Como <strong>administrador</strong>, este usuário tem acesso a todos os ambientes automaticamente.</>
                          : <>Este usuário tem <strong>acesso total</strong>. Todos os ambientes estão liberados automaticamente — não é necessário selecioná-los individualmente.</>}
                      </p>
                    </div>
                  ) : (
                    <>
                      {isNotificationOnlyUser && (
                        <div className="mb-3 px-4 py-3 rounded-xl bg-emerald-50 ring-1 ring-emerald-100 flex items-start gap-3">
                          <svg className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          <p className="text-xs text-emerald-800">
                            Este usuário será <strong>apenas informado</strong>. Nenhum ambiente está liberado para deploy.
                          </p>
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mb-2">
                        {form.targetIds.length} de {targetsAtivos.length} ambiente{targetsAtivos.length !== 1 ? "s" : ""} selecionado{form.targetIds.length !== 1 ? "s" : ""}
                      </p>

                      {loadingTargets ? (
                        <div className="flex items-center justify-center py-10">
                          <div className="w-6 h-6 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
                        </div>
                      ) : targetsAtivos.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-6">Nenhum ambiente disponível.</p>
                      ) : (
                        <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200 overflow-hidden">
                          {targetsAtivos.map((t) => {
                            const checked = form.targetIds.includes(t.id);
                            return (
                              <li key={t.id}>
                                <label className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                                  saving ? "cursor-not-allowed" : "cursor-pointer"
                                } ${checked ? "bg-violet-50/50" : !saving ? "hover:bg-slate-50" : ""}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleTarget(t.id)}
                                    disabled={saving}
                                    className="mt-1 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-2 focus:ring-violet-500 cursor-pointer disabled:cursor-not-allowed"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className={`font-semibold truncate ${checked ? "text-violet-900" : "text-slate-800"}`}>
                                        {t.displayName || t.siteName || `Ambiente #${t.id}`}
                                      </p>
                                      {t.isProduction && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-rose-100 text-rose-700 ring-1 ring-rose-200">
                                          Produção
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 flex-wrap">
                                      {t.serverName && <span className="truncate">{t.serverName}</span>}
                                      {t.siteName && t.siteName !== (t.displayName || "") && (
                                        <>
                                          <span className="text-slate-300">·</span>
                                          <span className="truncate font-mono">{t.siteName}</span>
                                        </>
                                      )}
                                      {t.applicationPath && (
                                        <>
                                          <span className="text-slate-300">·</span>
                                          <span className="truncate font-mono">{t.applicationPath}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  )}
                </Section>
              </div>

              {/* Save bar */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
                <p className="text-xs text-slate-500 min-w-0 truncate">
                  {isDirty ? "Há alterações pendentes de salvamento." : "Nenhuma alteração pendente."}
                </p>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isDirty || saving}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none shrink-0"
                  style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Salvar alterações
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
